import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Captures files pasted or dropped into the editor and hands them to the host,
 * which runs them through the upload modal.
 *
 * Accepts EVERY file type. This used to gate on `image/*` plus a four-entry MIME
 * allow-list, which is why dropping a docx/pdf/xlsx did nothing at all — the drop
 * fell through to the browser default (open the file in a new tab). Size is now
 * the only limit, matching the backend's own cap in `OneDriveAppService.UploadFile`.
 */

export type FileDropRejectReason = 'too-large' | 'empty';

export interface FileDropDetail {
    file: File;
    position: number | null;
    source: 'paste' | 'drop';
}

export interface FileDropRejectDetail {
    reason: FileDropRejectReason;
    fileName: string;
    fileSize: number;
    source: 'paste' | 'drop';
}

export interface FileDropHandlerOptions {
    maxFileSizeBytes: number;
    onFileDropped: (detail: FileDropDetail) => void;
    onReject: (detail: FileDropRejectDetail) => void;
    /**
     * Asked first, with EVERY file of the paste or drop: true = the host took them all (it validates them itself; the plugin
     * only stops the browser default), false = the single-file flow runs on the first file, as always. The editor answers true
     * in `fileDropMode` 'host' only (file-drop-mode.ts). Default: false.
     */
    onFilesDropped: (files: File[], source: 'paste' | 'drop') => boolean;
}

export const FileDropHandlerPluginKey = new PluginKey('vfFileDropHandler');

/** Matches the 25 MB ceiling enforced by the backend upload endpoint. */
export const DEFAULT_MAX_UPLOAD_BYTES: number = 25 * 1024 * 1024;

/** True when a drag carries OS files (as opposed to text or an in-editor node move). */
export function dataTransferHasFiles(dt: DataTransfer | null): boolean {
    if (!dt) {
        return false;
    }
    if (dt.files && dt.files.length > 0) {
        return true;
    }
    if (dt.types) {
        for (let i = 0; i < dt.types.length; i++) {
            if (dt.types[i] === 'Files') {
                return true;
            }
        }
    }
    return false;
}

export const FileDropHandler = Extension.create<FileDropHandlerOptions>({
    name: 'vfFileDropHandler',

    addOptions() {
        return {
            maxFileSizeBytes: DEFAULT_MAX_UPLOAD_BYTES,
            onFileDropped: () => undefined,
            onReject: () => undefined,
            onFilesDropped: () => false
        };
    },

    addProseMirrorPlugins() {
        const opts = this.options;

        const validateAndEmit = (
            file: File,
            position: number | null,
            source: 'paste' | 'drop'
        ): boolean => {
            const maxBytes: number = opts.maxFileSizeBytes || DEFAULT_MAX_UPLOAD_BYTES;

            if (!file.size) {
                opts.onReject({
                    reason: 'empty',
                    fileName: file.name || '',
                    fileSize: 0,
                    source
                });
                return true;
            }
            if (file.size > maxBytes) {
                opts.onReject({
                    reason: 'too-large',
                    fileName: file.name || '',
                    fileSize: file.size,
                    source
                });
                return true;
            }
            opts.onFileDropped({ file, position, source });
            return true;
        };

        return [
            new Plugin({
                key: FileDropHandlerPluginKey,

                props: {
                    handlePaste: (_view, event) => {
                        const clipboard = event.clipboardData;
                        if (!clipboard || !clipboard.files || clipboard.files.length === 0) {
                            return false;
                        }
                        // A host that takes every file ('host' mode) gets them all.
                        if (opts.onFilesDropped(Array.from(clipboard.files), 'paste')) {
                            event.preventDefault();
                            return true;
                        }
                        // Single file per paste.
                        const file: File = clipboard.files[0];
                        event.preventDefault();
                        validateAndEmit(file, null, 'paste');
                        return true;
                    },

                    handleDrop: (view, event, _slice, moved) => {
                        if (moved) {
                            return false;
                        }
                        const dt = (event as DragEvent).dataTransfer;
                        if (!dataTransferHasFiles(dt) || !dt.files || dt.files.length === 0) {
                            return false;
                        }
                        // A host that takes every file ('host' mode) gets them all; it has no use for the drop position.
                        if (opts.onFilesDropped(Array.from(dt.files), 'drop')) {
                            event.preventDefault();
                            return true;
                        }
                        const file: File = dt.files[0];
                        event.preventDefault();
                        const coords = { left: (event as DragEvent).clientX, top: (event as DragEvent).clientY };
                        const posAt = view.posAtCoords(coords);
                        const position: number | null = posAt ? posAt.pos : null;
                        validateAndEmit(file, position, 'drop');
                        return true;
                    },

                    handleDOMEvents: {
                        dragenter: (view, event) => {
                            const dt = (event as DragEvent).dataTransfer;
                            if (!dataTransferHasFiles(dt)) {
                                return false;
                            }
                            const dom = view.dom as HTMLElement;
                            dom.classList.add('vf-tiptap-drag-over');
                            return false;
                        },
                        dragover: (view, event) => {
                            const dt = (event as DragEvent).dataTransfer;
                            if (!dataTransferHasFiles(dt)) {
                                return false;
                            }
                            event.preventDefault();
                            const dom = view.dom as HTMLElement;
                            if (!dom.classList.contains('vf-tiptap-drag-over')) {
                                dom.classList.add('vf-tiptap-drag-over');
                            }
                            return false;
                        },
                        dragleave: (view, event) => {
                            const related = (event as DragEvent).relatedTarget as Node | null;
                            const dom = view.dom as HTMLElement;
                            // Only clear when the drag fully leaves the editor DOM.
                            if (!related || !dom.contains(related)) {
                                dom.classList.remove('vf-tiptap-drag-over');
                            }
                            return false;
                        },
                        drop: (view, _event) => {
                            const dom = view.dom as HTMLElement;
                            dom.classList.remove('vf-tiptap-drag-over');
                            return false;
                        }
                    }
                }
            })
        ];
    }
});
